import * as React from "react"
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../../lib/utils"

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    ref={ref}
    className={cn("flex flex-col gap-2", className)}
    {...props}
  />
))
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

// ── Radio indicator ──────────────────────────────────────────────────────────

const radioItemVariants = cva(
  [
    "peer shrink-0 rounded-full border-[1.5px] transition-all",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
    "disabled:cursor-not-allowed disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "h-4 w-4",
        md: "h-5 w-5",
      },
    },
    defaultVariants: { size: "sm" },
  }
)

export interface RadioGroupItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>,
    VariantProps<typeof radioItemVariants> {}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, size, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(
      radioItemVariants({ size }),
      // unchecked
      "border-[var(--ds-solid-neutral-7)] bg-white",
      "hover:ring-2 hover:ring-offset-1 hover:ring-[var(--ds-solid-neutral-4)]",
      // checked: filled with neutral-10
      "data-[state=checked]:border-[var(--ds-solid-neutral-10)] data-[state=checked]:bg-[var(--ds-solid-neutral-10)]",
      className
    )}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center w-full h-full">
      <span className="block rounded-full bg-white h-[5px] w-[5px]" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
))
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

// ── Convenience card-style radio row (used in Choropleth mode selector) ──────

export interface RadioCardProps {
  value: string
  label: string
  icon?: React.ReactNode
  disabled?: boolean
  className?: string
}

const RadioCard = React.forwardRef<HTMLButtonElement, RadioCardProps>(
  ({ value, label, icon, disabled, className }, ref) => (
    <RadioGroupPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(
        "group flex w-full items-center justify-between rounded-md border border-border px-3 py-2",
        "transition-colors cursor-pointer text-left",
        "hover:border-epic-neutral-9 hover:bg-muted/30",
        "data-[state=checked]:border-[var(--ds-solid-neutral-10)] data-[state=checked]:bg-muted/40",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
    >
      <span className="flex items-center gap-2">
        {/* custom circle indicator */}
        <span
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full border-[1.5px] h-4 w-4 transition-colors",
            "border-[var(--ds-solid-neutral-7)] bg-white",
            "group-data-[state=checked]:border-[var(--ds-solid-neutral-10)] group-data-[state=checked]:bg-[var(--ds-solid-neutral-10)]"
          )}
        >
          <RadioGroupPrimitive.Indicator>
            <span className="block rounded-full bg-white h-[5px] w-[5px]" />
          </RadioGroupPrimitive.Indicator>
        </span>
        <span className="font-mono text-[0.625rem] leading-none font-normal group-data-[state=checked]:font-semibold tracking-widest uppercase text-[var(--sidebar-sidebar-text-secondary,#5A6574)]">
          {label}
        </span>
      </span>
      {icon && (
        <span className="flex items-center text-muted-foreground">{icon}</span>
      )}
    </RadioGroupPrimitive.Item>
  )
)
RadioCard.displayName = "RadioCard"

export { RadioGroup, RadioGroupItem, RadioCard }
